// PatientContext.tsx
import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { PatientData } from "../types/admin/patient";
import { EpisodeResponse, ServiceTrackResponse } from "../dtos/patientmr/episode.dto";
import { TokenService } from "../services/auth/token.service";

// The shape of our Patient context
interface PatientContextType {
  selectedPatientId: string | number | null;
  setSelectedPatientId: React.Dispatch<React.SetStateAction<string | number | null>>;
  selectedPatient: PatientData | null;
  setSelectedPatient: React.Dispatch<React.SetStateAction<PatientData | null>>;
  clearSelectedPatient: () => void;
  patientUnselected: boolean;
  setPatientUnselected: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Call to trigger a dashboard refresh from any component (e.g. after track activation).
   * Components that own the dashboard data register via setRefreshDashboard.
   *
   * IMPORTANT: React useState stores functions by calling them if you pass a function directly.
   * Always double-wrap when setting: `setRefreshDashboard(() => () => myFn())`
   */
  refreshDashboard: (() => void) | null;
  setRefreshDashboard: React.Dispatch<React.SetStateAction<(() => void) | null>>;
  // Episode-related fields (optional)
  currentEpisodeId?: string;
  setCurrentEpisodeId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  currentEpisode?: EpisodeResponse | null;
  setCurrentEpisode?: React.Dispatch<React.SetStateAction<EpisodeResponse | null | undefined>>;
  serviceTracks?: ServiceTrackResponse[];
  setServiceTracks?: React.Dispatch<React.SetStateAction<ServiceTrackResponse[] | undefined>>;
}

// Create the context
const PatientContext = createContext<PatientContextType | undefined>(undefined);

// Provider component
interface PatientProviderProps {
  children: ReactNode;
}

export const PatientProvider: React.FC<PatientProviderProps> = ({ children }) => {
  const [selectedPatientId, setSelectedPatientId] = useState<string | number | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<PatientData | null>(null);
  const [patientUnselected, setPatientUnselected] = useState<boolean>(false);
  const [refreshDashboard, setRefreshDashboard] = useState<(() => void) | null>(null);
  const [currentEpisodeId, setCurrentEpisodeId] = useState<string | undefined>(undefined);
  const [currentEpisode, setCurrentEpisode] = useState<EpisodeResponse | null | undefined>(undefined);
  const [serviceTracks, setServiceTracks] = useState<ServiceTrackResponse[] | undefined>(undefined);

  useEffect(() => {
    if (selectedPatientId) {
      setPatientUnselected(false);
    } else {
      if (selectedPatient) {
        setPatientUnselected(true);
      }
    }
  }, [selectedPatientId, selectedPatient]);

  useEffect(() => {
    if (selectedPatient) {
      setPatientUnselected(false);
    } else {
      if (selectedPatientId) {
        setPatientUnselected(true);
      }
    }
  }, [selectedPatient, selectedPatientId]);

  // ISO-304: this provider sits above the auth switch, so it outlives a sign-out. When the tokens
  // are cleared (manual logout, idle timeout, failed refresh, logout in another tab) drop every
  // per-user value, so the next user never sees the previous user's patient.
  useEffect(() => TokenService.onChange((authenticated) => {
    if (authenticated) return;
    setSelectedPatientId(null);
    setSelectedPatient(null);
    setPatientUnselected(false);
    setRefreshDashboard(null);
    setCurrentEpisodeId(undefined);
    setCurrentEpisode(undefined);
    setServiceTracks(undefined);
  }), []);

  const clearSelectedPatient = () => {
    setPatientUnselected(true);
    setSelectedPatientId(null);
    setSelectedPatient(null);
  };

  return (
    <PatientContext.Provider
      value={{
        selectedPatientId,
        setSelectedPatientId,
        selectedPatient,
        setSelectedPatient,
        clearSelectedPatient,
        patientUnselected,
        setPatientUnselected,
        refreshDashboard,
        setRefreshDashboard,
        currentEpisodeId,
        setCurrentEpisodeId,
        currentEpisode,
        setCurrentEpisode,
        serviceTracks,
        setServiceTracks
      }}
    >
      {children}
    </PatientContext.Provider>
  );
};

// Custom hook for consuming the context
export const usePatient = () => {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error(
      "usePatient must be used within a PatientProvider. Make sure you are wrapping your app with <PatientProvider>."
    );
  }
  return context;
};
